/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React from 'react';
import styled from 'styled-components';
import { reduxConnect } from '../../common';

import { translate } from '../../common/translation/translate';
import { defaultTextValue } from '../../common/utilities/constants';
import { mediumMediaQuery } from '../../common/utilities/sizeHelpers';
import { CurrentHurricane, IStoreState } from '../common/interfaces/types';
import { isDataError } from '../common/utilities/isDataError';
import { HurricaneOutlookSummaryIconColours } from './constants';
import { HurricaneWidgetCaptionText } from './hurricaneWidgetCaptionText';
import { HurricaneWidgetNotificationIcon } from './hurricaneWidgetNotificationIcon';
import { CommonHurricaneWidgetUIProps, HurricaneWidgetType } from './types';
import { WidgetCaptionLoadingUI } from './widgetCaptionLoadingUI';

interface HurricaneWidgetCaptionViewProps extends Partial<CommonHurricaneWidgetUIProps> {
    currentHurricaneData?: CurrentHurricane;
    lastError: Record<string, unknown>;
    requestError: Record<string, unknown>;
    isNoHurricanes: boolean;
}

const mapStoreStateToProps = (storeState: IStoreState) => {
    const { hurricane, hurricaneOutlookSummary } = storeState || {};

    const { hurricaneIsFetching, currentHurricane, currentTrackPoint, lastError, hurricanes } = hurricane || {};
    const { isFetching: isFetchingHurricaneOutlookSummary, 
        requestError, 
        data: hurricaneOutlookSummaryData } = hurricaneOutlookSummary || {};
    const isNoHurricanes: boolean = hurricanes?.length === 0;

    return {
        isNoHurricanes,
        lastError,
        requestError,
        activeHurricaneData: currentTrackPoint,
        currentHurricaneData: currentHurricane, 
        isLoading: hurricaneIsFetching || isFetchingHurricaneOutlookSummary,
        hurricaneOutlookSummaryData: hurricaneOutlookSummaryData
    };
};

const mapDispatchToProps = {};

export const HurricaneWidgetCaptionView = reduxConnect(mapStoreStateToProps, mapDispatchToProps)(({ 
    isLoading,
    lastError,
    requestError,
    isNoHurricanes,
    hurricaneOutlookSummaryData, 
    currentHurricaneData,
    activeHurricaneData }: HurricaneWidgetCaptionViewProps) => {
    
    const isHurricaneDataError = isDataError(lastError);
    const isHurricanSummaryDataError = isDataError(requestError);
    const isNoHurricaneSummaryData = !hurricaneOutlookSummaryData?.o?.dc;
    // eslint-disable-next-line no-extra-parens
    const shouldDisplayErrorUI: boolean = (isNoHurricanes && isHurricanSummaryDataError) || (isNoHurricaneSummaryData && isHurricaneDataError) || (isHurricanSummaryDataError && isHurricaneDataError);
    
    if (shouldDisplayErrorUI) {
        return null; // No Caption when there's an error - should be handled differently.
    }

    let hurricaneWidgetDataType: typeof HurricaneWidgetType[keyof typeof HurricaneWidgetType] = HurricaneWidgetType.noActiveHurricaneOrWarning;
        
    const isActiveHurricane = !!currentHurricaneData && !!currentHurricaneData?.id;

    let captionTextUI = <HurricaneWidgetCaptionText hurricaneOutlookWidgetCaptionText={translate('noActiveHurricaneOrWarningIssued')} />;

    if (isLoading) {
        captionTextUI = <WidgetCaptionLoadingUI />;
    }

    if (isActiveHurricane && activeHurricaneData && !isLoading) {
        hurricaneWidgetDataType = HurricaneWidgetType.activeHurricane;
        const activeHurricaneName = currentHurricaneData?.type && currentHurricaneData?.name 
            ? `${currentHurricaneData.type} ${currentHurricaneData.name}` 
            : defaultTextValue;

        captionTextUI = <HurricaneWidgetCaptionText
            hurricaneIconType={hurricaneWidgetDataType}
            hurricaneWindSpeed={activeHurricaneData?.wind}
            hurricaneWindSpeedUnit={activeHurricaneData?.windUnit}
            hurricaneDistance={activeHurricaneData?.distance}
            hurricaneDistanceUnit={activeHurricaneData?.distanceUnit}
            regionEffectedByHurricane={activeHurricaneData?.basin}
            activeHurricaneName={activeHurricaneName}
            hurricaneCategory={activeHurricaneData?.category}
        />;
    }

    if (hurricaneOutlookSummaryData?.o?.dc && hurricaneOutlookSummaryData?.o?.dsc && !isActiveHurricane && !isLoading) {
        hurricaneWidgetDataType = HurricaneWidgetType.hurricaneOutlook;
        captionTextUI = <HurricaneWidgetCaptionText 
            hurricaneIconType={HurricaneWidgetType.hurricaneOutlook}
            hurricaneOutlookWidgetCaptionText={hurricaneOutlookSummaryData?.o?.dsc} />;
    }

    const notificationBellIconColor = hurricaneOutlookSummaryData?.o?.dc 
        ? HurricaneOutlookSummaryIconColours.activeStateYellow 
        : HurricaneOutlookSummaryIconColours.inactiveStateWhite;

    return (
        <WidgetMapCaption>
            <WidgetCaptionIconWrapper>
                <HurricaneWidgetNotificationIcon
                    iconColor={notificationBellIconColor} 
                    hurricaneIconType={hurricaneWidgetDataType} 
                    currentTrackPoint={activeHurricaneData}  />
            </WidgetCaptionIconWrapper>
            <WidgetCaptionTextWrapper>
                {captionTextUI}
            </WidgetCaptionTextWrapper>
        </WidgetMapCaption>
    );
});

const WidgetCaptionIconWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 30px;
    height: 100%;
`;

const WidgetCaptionTextWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
    align-items: center;
    width: calc(100% - 37px);
    height: 100%;
    padding-left: 7px;

    @media ${mediumMediaQuery} {
        padding-left: 4px;
    }
`;

const WidgetMapCaption = styled.figcaption`
    display: flex;
    flex-direction: row;
    padding: 4px 8px;
    height: 44px;
    width: 100%;
    background: #ffffff;
`;
